import React, { FC, useEffect } from 'react'

import { Box, Caption, ErrorMessage, InfiniteList } from '@sketch/components'

import NotificationListItem from './NotificationListItem'
import { NotificationListItemSkeleton } from './NotificationListItemSkeleton'
import { NotificationSearch } from './NotificationSearch/NotificationSearch'
import { WrapperList } from 'modules/user/views/Updates/Updates.styles'

import { ReactComponent as BellIcon } from '@sketch/icons/bell-filled-64'

import { NotificationInfoFragment } from '@sketch/gql-types'

/**
 * TYPES
 */
interface NotificationsListProps {
  loading: boolean
  loadMore: () => Promise<any>
  entries?: NotificationInfoFragment[]
  subscribeToMore: () => void
  markNotificationAsRead: (id: string) => void
  markAllNotificationAsSeen: () => void
  pageSize?: number
  onSearchSubmit: (search: string) => void
}

/**
 * MAIN COMPONENT
 */
const NotificationsList: FC<NotificationsListProps> = ({
  subscribeToMore,
  markAllNotificationAsSeen,
  markNotificationAsRead,
  entries,
  loading,
  loadMore,
  pageSize = 20,
  onSearchSubmit,
}) => {
  useEffect(() => {
    subscribeToMore()
    markAllNotificationAsSeen()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const renderList = () => {
    if (loading && !entries)
      return <NotificationListItemSkeleton pageSize={pageSize} />

    if (entries?.length === 0) {
      return (
        <Box width="100%" height="100%" px={6} UNSAFE_py="50px">
          <ErrorMessage
            icon={<BellIcon />}
            iconSize="medium"
            title="No updates"
            description="No unread comments or notifications yet."
          />
        </Box>
      )
    }

    const { unreadNotifications } = splitNotificationTypes(entries)

    const renderNotification = (notification: NotificationInfoFragment) => (
      <NotificationListItem
        key={notification.identifier}
        isRead={!!notification.isRead}
        notification={notification}
        markNotificationAsRead={() => {
          markNotificationAsRead(notification.identifier)
        }}
        isVisuallyRead={notification.isRead || false}
      />
    )

    return (
      <>
        <NotificationSearch onSearchSubmit={onSearchSubmit} />
        <InfiniteList
          onLoadMore={loadMore}
          renderLoading={() => <NotificationListItemSkeleton pageSize={5} />}
        >
          <WrapperList>
            {unreadNotifications.length ? (
              unreadNotifications.map(renderNotification)
            ) : (
              <Box px={2} py={6}>
                <Caption fontSize="E" textAlign="center" m={0}>
                  {'No unread notifications.'}
                </Caption>
              </Box>
            )}
          </WrapperList>
        </InfiniteList>
      </>
    )
  }

  return renderList()
}

export default NotificationsList

function splitNotificationTypes(
  notifications: NotificationInfoFragment[] = []
) {
  const notificationsFormat: {
    unreadNotifications: NotificationInfoFragment[]
    readNotifications: NotificationInfoFragment[]
  } = {
    unreadNotifications: [],
    readNotifications: [],
  }

  return (
    notifications.reduce(
      ({ unreadNotifications, readNotifications }, notification) => {
        if (notification.isRead) {
          readNotifications.push(notification)
        } else {
          unreadNotifications.push(notification)
        }

        return { unreadNotifications, readNotifications }
      },
      notificationsFormat
    ) || notificationsFormat
  )
}
